import { Timestamp } from "firebase/firestore";
import { SerializedTimestamp } from "../types/common";
import { ProcessStatus, STATUS_MAP, DEFAULT_STATUS_CONFIG } from "../types/status";

/**
 * 各種タイムスタンプ型を Date オブジェクトに変換する
 */
export function toJSDate(timestamp: Timestamp | Date | SerializedTimestamp | string | number | null | undefined): Date | null {
  if (timestamp === null || timestamp === undefined) return null;
  
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate();
  }
  if (timestamp instanceof Date) {
    return timestamp;
  }
  // 数値（ミリ秒タイムスタンプ）の場合
  if (typeof timestamp === "number") {
    return new Date(timestamp);
  }
  // ISO文字列の場合
  if (typeof timestamp === "string") {
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? null : date;
  }
  if (typeof timestamp === "object" && "_seconds" in (timestamp as unknown as Record<string, unknown>)) {
    return new Date((timestamp as SerializedTimestamp)._seconds * 1000);
  }
  
  return null;
}

/**
 * Firebase Timestamp または Date または JSON化されたTimestamp をフォーマットされた文字列に変換する
 */
export function formatTimestamp(timestamp: Timestamp | Date | SerializedTimestamp | string | number | null | undefined): string {
  const date = toJSDate(timestamp);
  if (!date) return "-";
  
  return new Intl.DateTimeFormat("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
}

/**
 * 日時フォーマット（秒なし）: YYYY/MM/DD HH:mm
 * フィルター表示、一覧表示などに使用
 */
export function formatDateTime(timestamp: Timestamp | Date | SerializedTimestamp | string | number | null | undefined): string {
  const date = toJSDate(timestamp);
  if (!date) return "-";
  
  return new Intl.DateTimeFormat("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

/**
 * 日付のみフォーマット: YYYY/MM/DD
 * セッション履歴、日付表示などに使用
 */
export function formatDate(timestamp: Timestamp | Date | SerializedTimestamp | string | number | null | undefined): string {
  const date = toJSDate(timestamp);
  if (!date) return "-";
  
  return new Intl.DateTimeFormat("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

/**
 * 時刻のみフォーマット: HH:mm:ss
 * ステップタイムラインなどに使用
 */
export function formatTime(timestamp: Timestamp | Date | SerializedTimestamp | string | number | null | undefined): string {
  const date = toJSDate(timestamp);
  if (!date) return "-";
  
  return new Intl.DateTimeFormat("ja-JP", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
}

/**
 * 時間単位フォーマット: H:00
 * グラフの時間軸などに使用
 */
export function formatHour(timestamp: Timestamp | Date | SerializedTimestamp | string | number | null | undefined): string {
  const date = toJSDate(timestamp);
  if (!date) return "-";
  
  return `${date.getHours()}:00`;
}

/**
 * 日時フォーマット（時間単位付き）: YYYY/MM/DD H:00
 * グラフのツールチップなどに使用
 */
export function formatDateHour(timestamp: Timestamp | Date | SerializedTimestamp | string | number | null | undefined): string {
  const date = toJSDate(timestamp);
  if (!date) return "-";
  
  const datePart = new Intl.DateTimeFormat("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
  
  return `${datePart} ${date.getHours()}:00`;
}

/**
 * 相対時間を取得する (例: 5分前)
 */
export function getRelativeTime(timestamp: Timestamp | Date | SerializedTimestamp | string | number | null | undefined): string {
  const date = toJSDate(timestamp);
  if (!date) return "-";

  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (diffInSeconds < 60) return "たった今";
  
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes}分前`;
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}時間前`;
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `${diffInDays}日前`;
  
  return formatTimestamp(date);
}

/**
 * ステータスに応じた色（Mantine theme color）を返す
 */
export function getStatusColor(status: string): string {
  const config = STATUS_MAP[status as ProcessStatus];
  return config ? config.color : DEFAULT_STATUS_CONFIG.color;
}

/**
 * ステータスの日本語ラベルを返す
 */
export function getStatusLabel(status: string): string {
  const config = STATUS_MAP[status as ProcessStatus];
  return config ? config.label : DEFAULT_STATUS_CONFIG.label;
}
